import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { History, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchaseHistoryPage = ({ user }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/inventory/all-purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(res.data);
    } catch (error) {
      toast.error('Failed to fetch purchase history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase History</h1>
          <p className="text-slate-600 mt-1">Complete purchase history for all inventory items</p>
        </div>
        <Button variant="outline" onClick={fetchPurchases}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Date</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Part Number</TableHead>
              <TableHead className="text-center">Quantity</TableHead>
              <TableHead className="text-right">Unit Price</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>PO #</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Added By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  No purchase history found
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((purchase) => (
                <TableRow key={purchase.id}>
                  <TableCell className="text-sm">
                    {purchase.created_at ? format(new Date(purchase.created_at), 'MMM d, yyyy') : '-'}
                  </TableCell>
                  <TableCell className="font-medium">{purchase.sku_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{purchase.sku_part_number || '-'}</Badge>
                  </TableCell>
                  <TableCell className="text-center">{purchase.quantity}</TableCell>
                  <TableCell className="text-right">
                    ₹{(purchase.unit_price || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    ₹{(purchase.total_price || (purchase.unit_price * purchase.quantity) || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {purchase.po_number || purchase.invoice_number || '-'}
                  </TableCell>
                  <TableCell>{purchase.vendor_name || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {purchase.created_by || '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PurchaseHistoryPage;
